import React, { useState, useEffect } from 'react';
import { SupervisionTable } from '@/components/supervision/SupervisionTable';
import { SupervisionForm } from '@/components/supervision/SupervisionForm';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { supabase } from '@/integrations/supabase/client';

// Import the logo
import CoachVaultLogo from '/lovable-uploads/f2df824b-d722-4548-a26c-7d738f754338.png';

interface SupervisionSession {
  id: string;
  supervisor_name: string;
  date: string;
  duration: number;
  topics: string[];
  notes: string;
  follow_up?: string;
  user_id: string;
}

const Supervision = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [sessionData, setSessionData] = useState<SupervisionSession | null>(null);
  
  const isNewSession = location.pathname === '/supervision/new';
  const isEditSession = location.pathname.includes('/supervision/edit/');
  
  // Fetch session data when editing
  useEffect(() => {
    if (isEditSession && id) {
      const fetchSession = async () => {
        setIsLoading(true);
        try {
          const { data, error } = await supabase
            .from('supervision_sessions')
            .select('*')
            .eq('id', id)
            .single();
          
          if (error) throw error;
          setSessionData(data);
        } catch (error) {
          console.error('Error fetching session:', error);
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchSession();
    }
  }, [id, isEditSession]);

  const handleAddSession = () => {
    navigate('/supervision/new');
  };

  if (isNewSession) {
    return <SupervisionForm />;
  }

  if (isEditSession) {
    return <SupervisionForm sessionToEdit={sessionData} isLoading={isLoading} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Supervision Sessions"
        description="Manage your coaching supervision sessions"
      >
        <Button 
          onClick={handleAddSession} 
          className="bg-coaching-primary hover:bg-coaching-primary/90 text-white py-2 px-4 rounded-md flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Session
        </Button>
      </PageHeader>
      
      <SupervisionTable />
    </div>
  );
};

export default Supervision;
